/**
 * Retailer related and upsell products (flag `retailer_related_products`,
 * MOT-705). Mirrors `src/types/relatedProducts.ts`; see
 * docs/internal/RELATED_PRODUCTS.md.
 */

export type RelationshipKind = 'store_related' | 'store_upsell' | 'auto_lens' | 'auto_accessory';
export const RELATIONSHIP_KINDS: readonly RelationshipKind[] = ['store_related', 'store_upsell', 'auto_lens', 'auto_accessory'];

export type ShopifyRelatedField =
  | 'motzu_compatible_lenses'
  | 'motzu_suggested_accessories'
  | 'discovery_related'
  | 'discovery_complementary';
export type BigCommerceRelatedField = 'bigcommerce_related';
export type RelatedField = ShopifyRelatedField | BigCommerceRelatedField;

export const SHOPIFY_RELATED_FIELDS: readonly ShopifyRelatedField[] = [
  'motzu_compatible_lenses',
  'motzu_suggested_accessories',
  'discovery_related',
  'discovery_complementary',
];

export type RelatedProductsDestination = 'motzu' | 'search_discovery' | 'both';
export type StorePicksMode = 'extend' | 'replace';

export interface RelatedProductsSettings {
  destination: RelatedProductsDestination;
  storePicks: StorePicksMode;
  mapping: Record<RelationshipKind, RelatedField[]>;
}

/** The server's defaults (`DEFAULT_RELATED_PRODUCTS_SETTINGS`), pinned by `relatedProductsSettings.test.ts`. */
export const DEFAULT_RELATED_PRODUCTS_SETTINGS: RelatedProductsSettings = {
  destination: 'motzu',
  storePicks: 'extend',
  mapping: {
    store_related: ['discovery_related', 'bigcommerce_related'],
    store_upsell: ['motzu_suggested_accessories', 'discovery_complementary', 'bigcommerce_related'],
    auto_lens: ['motzu_compatible_lenses', 'discovery_complementary'],
    auto_accessory: ['motzu_suggested_accessories', 'discovery_complementary'],
  },
};

export const MAX_PICKS_PER_KIND = 20;

export interface RelatedPickItem {
  id: string;
  name: string;
  brand: string;
  image: string | null;
  /** False when the product is no longer in the store's catalog. */
  available: boolean;
}

export interface StoreRelatedPicksView {
  productId: string;
  related: RelatedPickItem[];
  upsell: RelatedPickItem[];
  updatedAt: string | null;
  canEdit: boolean;
}

export interface ShopifyRelatedProductsStatus {
  readOk: boolean;
  discovery: Record<'related_products' | 'complementary_products', 'present' | 'missing' | 'wrong_type'>;
  motzu: Record<'compatible_lenses' | 'suggested_accessories', boolean>;
}

/** The stored setting with every missing or unknown part read as the default (the server's rule). */
export function normalizeRelatedProductsSettings(raw: unknown): RelatedProductsSettings {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const destination = (['motzu', 'search_discovery', 'both'] as const).includes(value.destination as RelatedProductsDestination)
    ? (value.destination as RelatedProductsDestination)
    : DEFAULT_RELATED_PRODUCTS_SETTINGS.destination;
  const storePicks = value.storePicks === 'replace' || value.storePicks === 'extend'
    ? value.storePicks
    : DEFAULT_RELATED_PRODUCTS_SETTINGS.storePicks;
  const rawMapping = value.mapping && typeof value.mapping === 'object' ? (value.mapping as Record<string, unknown>) : {};
  const known: readonly RelatedField[] = [...SHOPIFY_RELATED_FIELDS, 'bigcommerce_related'];
  const mapping = {} as Record<RelationshipKind, RelatedField[]>;
  for (const kind of RELATIONSHIP_KINDS) {
    const fields = rawMapping[kind];
    mapping[kind] = Array.isArray(fields)
      ? Array.from(new Set(fields.filter((f): f is RelatedField => known.includes(f as RelatedField))))
      : [...DEFAULT_RELATED_PRODUCTS_SETTINGS.mapping[kind]];
  }
  return { destination, storePicks, mapping };
}
